import { useRef, useState } from "react";
import { log } from "@connect/observability";
import { useVgsForm } from "../../../lib/vgs/use-vgs-form";
import type { VgsFormState } from "../../../lib/vgs/types";
import { saveCardCvc, tokenizeCvc } from "./cvc-api";
import type { CvcPhase, UseCvcConfirmationArgs } from "./use-cvc-confirmation.types";

export const CVC_FIELD_NAME = "cvc";
export const CVC_ERROR_CODE = "generic" as const;
export const CVC_ERROR_MESSAGE = "CVC confirmation failed";

export function useCvcConfirmation({
  client,
  cardId,
  apiKey,
  vgsEnvironment,
  onSuccess,
  onError,
}: UseCvcConfirmationArgs) {
  const [phase, setPhase] = useState<CvcPhase>("idle");
  const [valid, setValid] = useState(false);
  const [empty, setEmpty] = useState(true);
  const submittingRef = useRef(false);

  const {
    form,
    error: loadError,
    submit: vgsSubmit,
  } = useVgsForm({
    vgsEnvironment,
    onStateChange: (state: VgsFormState) => {
      setValid(!!state[CVC_FIELD_NAME]?.isValid);
      setEmpty(state[CVC_FIELD_NAME]?.isEmpty !== false);
    },
  });

  const submit = async () => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setPhase("submitting");

    let cardCvcToken: string;
    try {
      cardCvcToken = await tokenizeCvc(vgsSubmit, apiKey);
    } catch (err) {
      submittingRef.current = false;
      setPhase("idle");
      log.error("CVC tokenization failed", {
        reason: err instanceof Error ? err.message : String(err),
      });
      onError({ code: CVC_ERROR_CODE, message: CVC_ERROR_MESSAGE });
      return;
    }

    // The token is also returned to the caller, so a failed save is only logged.
    try {
      await saveCardCvc(client, cardId, cardCvcToken);
    } catch (err) {
      log.error("Saving the card CVC failed", {
        reason: err instanceof Error ? err.message : String(err),
      });
    }
    setPhase("succeeded");
    onSuccess({ cardCvcToken });
  };

  return { form, loadError, phase, valid, empty, submit };
}
